import {
  BookmarkFlag,
  type Ingredient,
} from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';

import { forwardRef, useState } from 'react';
import { cn } from '@/utils/styles';
import { Link } from '@design-system/Link';
import { Card, CardTitle } from '@design-system/Card';
import { BookmarkButton } from '@/components/shared/BookmarkButton';
import { getIngredientDetailsPageUrl } from '@/utils/nutrition/getIngredientDetailsPageUrl';
import { useSaveSupplementMutation } from '@viome/viome-base/src/usecase/Nutrition/NutritionQuery';

export interface MyIngredientsCardProps extends React.ComponentProps<'div'> {
  productId: number;
  ingredient: Ingredient;
}

const sanitizeIngredientName = (name?: string) => {
  return name?.replace('_', '');
};

export const MyIngredientsCard = forwardRef<
  HTMLDivElement,
  MyIngredientsCardProps
>(({ className, productId, ingredient, ...rest }, ref) => {
  const ingredientDetailsPageUrl = getIngredientDetailsPageUrl(ingredient.id);
  const sanitizedIngredientName = sanitizeIngredientName(ingredient.name);

  const [isBookmarked, setIsBookmarked] = useState(
    Boolean(ingredient.is_bookmarked),
  );

  const handleOnSaveMutationSuccess = () => {
    setIsBookmarked((previousValue) => !previousValue);
  };

  const { mutate, isPending } = useSaveSupplementMutation({
    onSuccess: handleOnSaveMutationSuccess,
  });

  const handleOnBookmark: React.MouseEventHandler<HTMLButtonElement> = (
    event,
  ) => {
    event.stopPropagation();

    const bookmarkFlag = isBookmarked ? BookmarkFlag.Remove : BookmarkFlag.Add;

    mutate({
      flag: bookmarkFlag,
      product_id: productId,
      ingredient_id: ingredient.id,
    });
  };

  return (
    <div ref={ref} className={cn('relative min-h-16', className)}>
      <Link
        variant="flat"
        to={ingredientDetailsPageUrl}
        state={{
          isBookmarked: isBookmarked,
          productId: productId,
        }}
      >
        <Card className="h-full p-5" {...rest}>
          <CardTitle className="flex flex-row items-center justify-between gap-4">
            <div className="flex flex-col gap-2 w-full overflow-hidden pr-10">
              <span className="typography-title text-primary truncate">
                <abbr className="no-underline" title={sanitizedIngredientName}>
                  {sanitizedIngredientName}
                </abbr>
              </span>

              {ingredient.volume && (
                <span className="typography-caption1 text-tertiary truncate">
                  {ingredient.volume}
                </span>
              )}
            </div>
          </CardTitle>
        </Card>
      </Link>

      <BookmarkButton
        size="icon"
        variant="ghost"
        disabled={isPending}
        isPending={isPending}
        bookmarked={isBookmarked}
        onClick={handleOnBookmark}
        className="absolute right-3 top-1/2 -translate-y-1/2"
      />
    </div>
  );
});
